// src/utils/apiFetch.js
// Authenticated fetch wrapper — mirrors the axios interceptor in useAuth.js
// Covers all raw fetch() calls that axios interceptors cannot see.
//
// Handles:
//   - Automatic token injection on every request
//   - 401 → silent refresh via httpOnly refresh cookie → retry original request
//   - Concurrent 401 deduplication — one refresh in flight, all callers retry
//   - Refresh token expired → clean logout via _onLogout callback
//   - Guest requests (no token) — pass through unchanged
//   - Public endpoints — pass through unchanged
//
// D3: chart-signals log, drawings, indicator-layouts all use fetch()
// Without this, every D3 signal call silently breaks after 24h token expiry.
//
// Usage in any component:
//   import apiFetch from '../utils/apiFetch';
//   const fetch = apiFetch;  // one line — all fetch() calls in module auto-covered

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

let _token          = null;   // current access token — synced by useAuth
let _refreshFlight  = null;   // deduplicate concurrent 401s
let _onRefresh      = null;   // callback: new token → update React state in useAuth
let _onLogout       = null;   // callback: refresh failed → logout user cleanly

// ── Called by useAuth.useEffect to wire auth lifecycle ────────────────────────
export function configureApiFetch({ token, onRefresh, onLogout }) {
  _token     = token;
  _onRefresh = onRefresh;
  _onLogout  = onLogout;
}

// ── Called whenever token changes (login, refresh, logout) ───────────────────
export function updateApiFetchToken(token) {
  _token = token;
}

// ── Core fetch wrapper ────────────────────────────────────────────────────────
export async function apiFetch(url, options = {}) {
  // Inject token into Authorization header if available and not already set
  const headers = { ...(options.headers || {}) };
  if (_token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${_token}`;
  }

  // Make the original request
  let response = await window.fetch(url, { ...options, headers });

  // Not a 401 — return as-is (covers: success, 403, 404, 500, guest calls, public endpoints)
  if (response.status !== 401) return response;

  // No token means we're a guest — 401 is expected, do not refresh
  if (!_token) return response;

  // If the failing call IS the refresh endpoint — refresh token is also dead → logout
  if (url.includes('/api/auth/refresh')) {
    _onLogout?.();
    return response;
  }

  // ── Silent refresh ────────────────────────────────────────────────────────
  // Deduplicate: if multiple fetch() calls 401 at the same moment,
  // only ONE refresh request is made. All callers share the same promise.
  if (!_refreshFlight) {
    _refreshFlight = window.fetch(`${API_URL}/api/auth/refresh`, {
      method:      'POST',
      credentials: 'include',  // sends httpOnly refresh token cookie automatically
      headers:     { 'Content-Type': 'application/json' },
    })
    .then(async r => {
      if (!r.ok) {
        // Refresh endpoint returned error (refresh token expired/invalid)
        _onLogout?.();
        throw new Error('Refresh failed — logging out');
      }
      const data = await r.json();
      const newToken = data.token;
      _token = newToken;                  // update module-level token immediately
      _onRefresh?.(newToken);             // update React state + localStorage in useAuth
      return newToken;
    })
    .catch(err => {
      _onLogout?.();
      throw err;
    })
    .finally(() => {
      _refreshFlight = null;              // allow next 401 to trigger a new refresh
    });
  }

  // Wait for the single in-flight refresh, then retry original request
  try {
    const newToken = await _refreshFlight;
    const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
    return window.fetch(url, { ...options, headers: retryHeaders });
  } catch {
    // Refresh failed — logout was already called, return original 401 response
    return response;
  }
}

export default apiFetch;
